class TooltipPositioner {
    constructor(props, container, tooltip) {
        this.VISUAL_OFFSET = 28;
        this.VISUAL_OFFSET_MIN = 8;
        this.ARROW_WIDTH = 16;
        this.BODY_MARGIN = 16; // @spacing-2x

        this.props = props;
        this.containerBounds = container.getBoundingClientRect();
        this.tooltipBounds = this._getCorrectTooltipBounds(tooltip);

        const safePlacement = this.getSafePlacement();

        this.tooltipPosition = this._getTooltipPosition(safePlacement);
        this.arrowPosition = this._getTooltipArrowPosition(safePlacement);
    }

    /**
     * Get the adjusted positions for the arrow and the tooltip
     * @return {object} { tooltipPosition, arrowPosition }
     */
    getPositions() {
        return {
            tooltipPosition: this.tooltipPosition,
            arrowPosition: this.arrowPosition
        };
    }

    /**
     * Find out if the tooltip would overflow in the passed placement property.
     * It would find the best placement for the tooltip.
     * @return {string} Safe placement
     */
    getSafePlacement() {
        const {containerBounds, tooltipBounds} = this;
        const {tooltipType, placement, useClientHeight} = this.props;
        const bodyBounds = document.body.getBoundingClientRect();

        // Using window.innerHeight instead of clientHeight would introduce a bug
        // because if the tooltip overflows, it changes window.innerHeight and we
        // are therefore not able to calculate the overflow.
        // https://github.homeawaycorp.com/ui-toolkit/react-tooltip/pull/47#discussion_r239171
        const clientHeight = document.documentElement.clientHeight || document.body.clientHeight;
        const windowHeight = (tooltipType === 'tooltip' || tooltipType === 'tooltip--lg' || useClientHeight) ? clientHeight : bodyBounds.height;

        const allowedPlacements = {
            bottom: containerBounds.bottom + tooltipBounds.height < windowHeight,
            top: containerBounds.top - tooltipBounds.height > bodyBounds.top + window.pageYOffset,
            right: containerBounds.right + tooltipBounds.width < bodyBounds.width,
            left: containerBounds.left - tooltipBounds.width > bodyBounds.left
        };

        if (!allowedPlacements[placement]) {
            if (placement === 'bottom' && allowedPlacements.top) {
                return 'top';
            } else if (placement === 'top' && allowedPlacements.bottom) {
                return 'bottom';
            } else if (placement === 'right' && allowedPlacements.left) {
                return 'left';
            } else if (placement === 'left' && allowedPlacements.right) {
                return 'right';
            }
            return Object.keys(allowedPlacements).find((key) => allowedPlacements[key]) || placement;
        }

        return placement;
    }

    /**
     * Temporarily add fixed positioning to the tooltip to get correct bounds
     * (Absolute positioning affect the width and height of tooltip if triggering element is small)
     * If using fullWidth, don't add fixed positioning because it changes line wrapping,
     * which makes height calculations wrongly think the entire tooltip is one line in height
     * @return {object} { left, top, right, bottom, x, y, width, height }
     */
    _getCorrectTooltipBounds(tooltip) {
        if (!this.props.fullWidth) {
            tooltip.style.position = 'fixed';
        }

        const relativeBounds = tooltip.getBoundingClientRect();

        tooltip.style.position = '';

        return relativeBounds;
    }

    /**
     * Calculate the position of the tooltip, taking into account the potential
     * overflows to the right and left. This will ensure that the tooltip is
     * always visible
     * @return {object} Styles for the tooltip ({left, top, width})
     */
    _getTooltipPosition(placement) {
        const {containerBounds, tooltipBounds} = this;
        const leftOffset = this.props.parentNode ? containerBounds.left + window.pageXOffset : 0;
        const topOffset = this.props.parentNode ? containerBounds.top + window.pageYOffset : 0;
        const buttonHeightOffset = (tooltipBounds.height < containerBounds.height) ? (tooltipBounds.height / 4) : 0;
        const centeredHeightOffset = (this.props.centered) ? -((tooltipBounds.height / 4) - 12) : 0;
        let position = {
            left: '',
            top: '',
            width: this.props.fullWidth ? this.containerBounds.width : this.tooltipBounds.width
        };
        if (placement === 'left') {
            position.left = leftOffset - this.tooltipBounds.width;
            position.top = topOffset - (tooltipBounds.height / 4) + centeredHeightOffset + buttonHeightOffset;
        } else if (placement === 'top') {
            const tooltipLeft = this.props.fullWidth ? 0 : this._getHorizontalTooltipLeft();
            position.left = leftOffset + tooltipLeft;
            position.top = topOffset - tooltipBounds.height;
            position.arrowLeft = this._getHorizontalTooltipArrowLeft(-this._getHorizontalTooltipLeft());
        } else if (placement === 'bottom') {
            const tooltipLeft = this.props.fullWidth ? 0 : this._getHorizontalTooltipLeft();
            position.left = leftOffset + tooltipLeft;
            position.top = topOffset + containerBounds.height;
            position.arrowLeft = this._getHorizontalTooltipArrowLeft(-this._getHorizontalTooltipLeft());
        } else {
            // safePlacement === Right
            position.left = leftOffset + containerBounds.width;
            position.top = topOffset - (tooltipBounds.height / 4) + centeredHeightOffset + buttonHeightOffset;
        }

        return position;
    }

    /**
     * Calculate the styles for the arrow, so it would always look visually nice.
     * @return {object} Arrow styles ({left, top})
     */
    _getTooltipArrowPosition(safePlacement) {
        const {tooltipType} = this.props;
        let position = {};

        const isLargeTooltip = (tooltipType === 'tooltip--lg' || tooltipType === 'popover');
        const isHorizontalTooltip = (/left|right/).test(safePlacement);

        if (isHorizontalTooltip && isLargeTooltip) {
            const arrowTopOffset =
                this.tooltipBounds.height / 4 + this.containerBounds.height / 2;
            position.top = `${arrowTopOffset}px`;
        }

        if (!isHorizontalTooltip) {
            position.left = this.tooltipPosition.arrowLeft;
        }

        if (this.props.centered && isHorizontalTooltip) {
            position.top = '50%';
        }

        return position;
    }

    /**
     * Calculates the tooltip left style, taking into account that if it
     * would overflow on the left or right
     * @return {number} Left position of the horizontal tooltip
     */
    _getHorizontalTooltipLeft() {
        const {containerBounds, tooltipBounds} = this;

        if (this.props.centered && !this._willCenteredTooltipOverflow()) {
            return containerBounds.width / 2 - tooltipBounds.width / 2;
        }

        const initialLeftPosition = containerBounds.width / 2 - this.ARROW_WIDTH / 2 - this.VISUAL_OFFSET;

        const left = containerBounds.left + initialLeftPosition;
        const overflowLeft = this.BODY_MARGIN - left;

        const right = left + tooltipBounds.width;
        // Using window.innerWidth instead of clientWidth would introduce a bug
        // because if the tooltip overflows, it changes window.innerWidth and we
        // are therefore not able to calculate the overflow.
        // https://github.homeawaycorp.com/ui-toolkit/react-tooltip/pull/47#discussion_r239171
        const clientWidth = document.documentElement.clientWidth || document.body.clientWidth;
        const overflowRight = right - (clientWidth - this.BODY_MARGIN);

        if (overflowRight < 0 && overflowLeft < 0) {
            return initialLeftPosition;
        } else if (overflowRight > 0) {
            return initialLeftPosition - overflowRight;
        }
        return initialLeftPosition + overflowLeft;
    }

    /**
     * Calculate left style for the arrow, if the tooltip is horizontal.
     * @param  {number} offset pixels to offset the arrow
     * @return {string} left style string. Ex: "20px"
     */
    _getHorizontalTooltipArrowLeft(offset) {
        const {containerBounds, tooltipBounds} = this;
        if (this.props.centered && !this._willCenteredTooltipOverflow()) {
            return '50%';
        }

        const arrowLeftContainerCenter = offset + containerBounds.width / 2;

        // Set lower and upper limit for the arrow position not to
        // overflow the tooltip width
        const pixels = Math.min(
            tooltipBounds.width - this.ARROW_WIDTH / 2 - this.VISUAL_OFFSET_MIN,
            Math.max(this.ARROW_WIDTH / 2 + this.VISUAL_OFFSET_MIN, arrowLeftContainerCenter)
        );
        return `${pixels}px`;
    }

    /**
     * Helper function that will check if the "centered" tooltip would overflow
     * on the right and left
     * @return {boolean} Would centered tooltip overflow?
     */
    _willCenteredTooltipOverflow() {
        const {containerBounds, tooltipBounds} = this;
        const centeredTooltipRight = containerBounds.left + containerBounds.width / 2 + tooltipBounds.width / 2;

        // Using window.innerWidth instead of clientWidth would introduce a bug
        // because if the tooltip overflows, it changes window.innerWidth and we
        // are therefore not able to calculate the overflow.
        // https://github.homeawaycorp.com/ui-toolkit/react-tooltip/pull/47#discussion_r239171
        const clientWidth = document.documentElement.clientWidth || document.body.clientWidth;
        const overflowsRight = (clientWidth - this.BODY_MARGIN) - centeredTooltipRight < 0;

        const centeredTooltipLeft = containerBounds.left + containerBounds.width / 2 - tooltipBounds.width / 2;
        const overflowsLeft = centeredTooltipLeft - (0 + this.BODY_MARGIN) < 0;

        return overflowsRight || overflowsLeft;
    }
}

export default TooltipPositioner;
